import React, { useCallback, useState, useRef, useEffect } from 'react'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import { MapPin, Loader2, Search } from 'lucide-react'

const MAP_CONTAINER_STYLE = { width: '100%', height: '320px', borderRadius: '12px' }
const DEFAULT_CENTER = { lat: 30.0869, lng: 78.2676 } // Rishikesh, India

export interface MapLocation {
  lat: number
  lng: number
}

interface MapLocationPickerProps {
  value: MapLocation | null
  onChange: (location: MapLocation | null) => void
  placeholder?: string
  /** Optional initial address to pre-fill the search (e.g. from parent form) */
  initialAddress?: string
}

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  value,
  onChange,
  placeholder = 'Click on the map to set your school location',
  initialAddress = '',
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [addressQuery, setAddressQuery] = useState(initialAddress)
  const [geocoding, setGeocoding] = useState(false)
  const [geocodeError, setGeocodeError] = useState<string | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
  })

  // Sync search box when parent form fields change (e.g. address, city, country)
  useEffect(() => {
    if (initialAddress) setAddressQuery(initialAddress)
  }, [initialAddress])

  // Initialize Geocoder as soon as Maps API is loaded (no need to wait for map)
  useEffect(() => {
    if (isLoaded && typeof google !== 'undefined') {
      geocoderRef.current = new google.maps.Geocoder()
    }
  }, [isLoaded])

  const updateMarker = useCallback(
    (mapInstance: google.maps.Map | null, pos: { lat: number; lng: number } | null) => {
      if (markerRef.current) {
        markerRef.current.setMap(null)
        markerRef.current = null
      }
      if (mapInstance && pos) {
        const marker = new google.maps.Marker({
          position: pos,
          map: mapInstance,
          title: 'School location',
          optimized: false,
        })
        markerRef.current = marker
      }
    },
    []
  )

  const onMapLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance)
      if (value) {
        updateMarker(mapInstance, { lat: Number(value.lat), lng: Number(value.lng) })
      }
    },
    [value, updateMarker]
  )

  useEffect(() => {
    if (map) {
      updateMarker(map, value ? { lat: Number(value.lat), lng: Number(value.lng) } : null)
    }
  }, [map, value, updateMarker])

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null)
        markerRef.current = null
      }
    }
  }, [])

  const searchAddress = useCallback(async () => {
    const query = addressQuery.trim()
    if (!query) return
    if (!geocoderRef.current) return

    setGeocoding(true)
    setGeocodeError(null)
    try {
      const result = await geocoderRef.current.geocode({ address: query })
      const first = result.results[0]
      if (!first?.geometry?.location) {
        setGeocodeError('Address not found. Try a more specific location.')
        return
      }
      const lat = first.geometry.location.lat()
      const lng = first.geometry.location.lng()
      onChange({ lat, lng })
      if (map) {
        map.panTo({ lat, lng })
        map.setZoom(15)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not find this address.'
      setGeocodeError(msg.includes('Geocoding') || msg.includes('403') || msg.includes('denied')
        ? 'Geocoding API may not be enabled. Enable it in Google Cloud Console.'
        : 'Could not find this address. Please try again.')
    } finally {
      setGeocoding(false)
    }
  }, [addressQuery, map, onChange])

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat()
      const lng = e.latLng?.lng()
      if (lat != null && lng != null) {
        onChange({ lat, lng })
      }
    },
    [onChange]
  )

  const center = value ?? DEFAULT_CENTER

  if (!apiKey || apiKey.trim() === '') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{placeholder}</p>
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <MapPin className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-2">
            Add <code className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">VITE_GOOGLE_MAPS_API_KEY</code> to your .env file to enable the map picker.
          </p>
          <p className="text-xs text-gray-500">
            Or enter coordinates manually below (optional)
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 max-w-xs mx-auto">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                placeholder="e.g. 30.0869"
                value={value?.lat ?? ''}
                onChange={(e) => {
                  const lat = parseFloat(e.target.value)
                  if (!isNaN(lat)) {
                    onChange({ lat, lng: value?.lng ?? DEFAULT_CENTER.lng })
                  } else if (value?.lng != null) {
                    onChange(null)
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                placeholder="e.g. 78.2676"
                value={value?.lng ?? ''}
                onChange={(e) => {
                  const lng = parseFloat(e.target.value)
                  if (!isNaN(lng)) {
                    onChange({ lat: value?.lat ?? DEFAULT_CENTER.lat, lng })
                  } else if (value?.lat != null) {
                    onChange(null)
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="rounded-xl border-2 border-dashed border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">Failed to load Google Maps. Please check your API key.</p>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-2" />
        <p className="text-sm text-gray-600">Loading map...</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">{placeholder}</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={addressQuery}
            onChange={(e) => {
              setAddressQuery(e.target.value)
              setGeocodeError(null)
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
            placeholder="Search address or place..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
          />
        </div>
        <button
          type="button"
          onClick={searchAddress}
          disabled={geocoding || !addressQuery.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
        >
          {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Find
        </button>
      </div>
      {geocodeError && <p className="text-sm text-red-600">{geocodeError}</p>}
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={center}
          zoom={value ? 15 : 5}
          onClick={onMapClick}
          onLoad={onMapLoad}
          options={{
            scrollwheel: true,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        />
      </div>
      {value && (
        <p className="text-xs text-gray-500">
          Location: {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          <button
            type="button"
            onClick={() => onChange(null)}
            className="ml-2 text-primary-600 hover:text-primary-700"
          >
            Clear
          </button>
        </p>
      )}
    </div>
  )
}

export default MapLocationPicker
